/**
 * EditDialog — Modal "Editar Canción"
 *
 * Extraído de App.js líneas 8519-8684 durante la Ola C selectiva del refactor 2026-02.
 * Encapsula el formulario completo de edición (título/artista/BPM/año/energía/
 * estilos/momentos/valoración/notas/favorito/denon-pendiente). Sin lógica nueva.
 *
 * El estado `formData` y el handler `handleEdit` siguen viviendo en App.js
 * y se inyectan por props para no alterar comportamiento.
 */

import axios from 'axios';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Button } from './ui/button';
import { MultiSelect } from '../MultiSelect';
import StarRating from './StarRating';
import { API_URL } from '../config';

const API = API_URL;

export const EditDialog = ({
  isOpen,
  onOpenChange,
  formData,
  setFormData,
  onSubmit,
  getAllGenres,
  getAllMomentos,
}) => {
  const handleSuggestEnergia = async () => {
    try {
      const response = await axios.post(`${API}/songs/suggest-energia`, {
        bpm: formData.bpm ? parseInt(formData.bpm, 10) : null,
        genres: formData.genres,
      });
      if (response.data.energia) {
        setFormData({ ...formData, energia: response.data.energia });
        toast.success(`Energía sugerida: ${response.data.energia}`);
      }
    } catch {
      toast.error('Error al sugerir energía');
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-slate-800 border-slate-700 text-white w-[95vw] max-w-md max-h-[90vh] overflow-y-auto"
        data-testid="edit-song-dialog"
      >
        <DialogHeader>
          <DialogTitle className="text-lg text-white">Editar Canción</DialogTitle>
        </DialogHeader>
        <form onSubmit={onSubmit} className="space-y-3">
          <div>
            <Label htmlFor="edit-title">Título *</Label>
            <Input
              id="edit-title"
              className="bg-slate-900 border-slate-700 text-white"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              required
              data-testid="edit-song-title-input"
            />
          </div>

          <div>
            <Label htmlFor="edit-artist">Artista</Label>
            <Input
              id="edit-artist"
              className="bg-slate-900 border-slate-700 text-white"
              value={formData.artist}
              onChange={(e) => setFormData({ ...formData, artist: e.target.value })}
              data-testid="edit-song-artist-input"
            />
          </div>

          <div>
            <Label htmlFor="edit-bpm">BPM</Label>
            <Input
              id="edit-bpm"
              type="number"
              className="bg-slate-900 border-slate-700 text-white"
              value={formData.bpm}
              onChange={(e) => setFormData({ ...formData, bpm: e.target.value })}
              min="1"
            />
          </div>

          <div>
            <Label htmlFor="edit-year">Año</Label>
            <Input
              id="edit-year"
              type="number"
              className="bg-slate-900 border-slate-700 text-white"
              value={formData.year}
              onChange={(e) => setFormData({ ...formData, year: e.target.value })}
              min="1900"
              max="2100"
              placeholder="2024"
            />
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <Label htmlFor="edit-energia">Energía</Label>
              <button
                type="button"
                onClick={handleSuggestEnergia}
                className="text-xs text-purple-400 hover:text-purple-300 underline"
                data-testid="edit-song-suggest-energia"
              >
                ✨ Sugerir Automático
              </button>
            </div>
            <select
              id="edit-energia"
              className="w-full p-2 bg-slate-900 border border-slate-700 rounded text-white"
              value={formData.energia || ''}
              onChange={(e) => setFormData({ ...formData, energia: e.target.value })}
            >
              <option value="">Sin especificar</option>
              <option value="Baja">Baja</option>
              <option value="Media">Media</option>
              <option value="Alta">Alta</option>
              <option value="Muy Alta">Muy Alta</option>
            </select>
          </div>

          <div>
            <Label>Estilos (máx 3)</Label>
            <MultiSelect
              options={getAllGenres()}
              selected={formData.genres}
              onChange={(genres) => setFormData({ ...formData, genres })}
              maxItems={3}
              placeholder="Seleccionar estilos"
            />
          </div>

          <div>
            <Label>Momentos de Boda (máx 3)</Label>
            <MultiSelect
              options={getAllMomentos()}
              selected={formData.momentos}
              onChange={(momentos) => setFormData({ ...formData, momentos })}
              maxItems={3}
              placeholder="Seleccionar momentos"
            />
          </div>

          <div>
            <Label>Valoración</Label>
            <div className="mt-2">
              <StarRating
                rating={formData.rating || 0}
                onRate={(rating) => setFormData({ ...formData, rating })}
                editable
              />
            </div>
          </div>

          <div>
            <Label htmlFor="edit-notas">Notas</Label>
            <Textarea
              id="edit-notas"
              className="bg-slate-900 border-slate-700 text-white min-h-[60px]"
              value={formData.notas}
              onChange={(e) => setFormData({ ...formData, notas: e.target.value })}
              placeholder="Ej: Perfecta para vals..."
            />
          </div>

          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              id="edit-favorito"
              className="w-4 h-4"
              checked={formData.favorito}
              onChange={(e) => setFormData({ ...formData, favorito: e.target.checked })}
            />
            <Label htmlFor="edit-favorito" className="cursor-pointer">
              Marcar como favorito
            </Label>
          </div>

          <div className="flex items-center space-x-2 bg-orange-900/20 p-3 rounded border border-orange-700">
            <input
              type="checkbox"
              id="edit-pendiente_denon"
              className="w-4 h-4"
              checked={formData.pendiente_denon}
              onChange={(e) =>
                setFormData({ ...formData, pendiente_denon: e.target.checked })
              }
            />
            <Label
              htmlFor="edit-pendiente_denon"
              className="cursor-pointer text-orange-300"
            >
              🎧 Pendiente añadir a Denon Engine DJ
            </Label>
          </div>

          <Button
            type="submit"
            className="w-full bg-cyan-500 hover:bg-cyan-600"
            data-testid="edit-song-submit-btn"
          >
            Actualizar Canción
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default EditDialog;
